import { MoreVert as MoreIcon } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ActionMenu from "../../../common/components/functional/actionMenu/ActionMenu";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../common/helpers/isEmpty";
import { Nullable } from "../../../common/types/nullable";
import { collectConversationAttachments } from "../attachments/messageAttachments";
import { getConversationActionMenuItems } from "../conversation/conversationActions";
import ConversationAttachmentsList from "../conversation/ConversationAttachmentsList";
import ConversationDropZone from "../conversation/ConversationDropZone";
import ConversationParticipantHeader from "../conversation/ConversationParticipantHeader";
import ConversationThreadTabs, {
  ConversationThreadTab,
} from "../conversation/ConversationThreadTabs";
import Messages from "../conversation/Messages";
import MessagingComposer from "../conversation/MessagingComposer";
import ForwardConversationDialog from "../ForwardConversationDialog";
import MessagingToolbar from "../MessagingToolbar";
import { getMaximizeMenuItem } from "../messagingViewModeActions";
import ResolveConversationDialog from "../ResolveConversationDialog";
import { ParticipantOption } from "../types/common";
import useMessagingThread from "../useMessagingThread";
import useMessagingViewMode from "../useMessagingViewMode";

type Props = {
  participant: Nullable<ParticipantOption>;
  onAssignedToMe?: () => void;
  onBack: () => void;
};

const FloatingChatConversation = ({
  participant,
  onAssignedToMe,
  onBack,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [threadTab, setThreadTab] =
    useState<ConversationThreadTab>("conversation");
  const { t } = useTranslation(["messaging", "common"]);
  const { maximize } = useMessagingViewMode();
  const theme = useTheme();

  const {
    messages,
    messagesLoading,
    messagesLoadingMore,
    loadMore,
    virtuosoRef,
    draft,
    onDraftChange,
    onSend,
    sendingDisabled,
    inputDisabled,
    composerPlaceholder,
    conversationTitle,
    titleLocked,
    onTitleChange,
    isCreatingConversation,
    isAssigning,
    canAssignConversation,
    isClosedConversation,
    canMarkAsDone,
    canTransfer,
    teamTransferDisabled,
    assignToMe,
    markAsDone,
    transfer,
    selectedConversationId,
    pendingFiles,
    addPendingFiles,
    removePendingFile,
  } = useMessagingThread({ participant, onAssignedToMe });

  useEffect(() => {
    setResolveOpen(false);
    setForwardOpen(false);
    setThreadTab("conversation");
  }, [selectedConversationId]);

  const menuItems = [
    ...getConversationActionMenuItems({
      t,
      availability: {
        canAssignConversation,
        canMarkAsDone,
        canTransferConversation: canTransfer,
        isClosed: isClosedConversation,
      },
      handlers: {
        assignToMe,
        markAsDone: () => setResolveOpen(true),
        onForward: () => setForwardOpen(true),
      },
    }),
    getMaximizeMenuItem({
      t: (key) => t(`messaging:${key}`),
      onClick: maximize,
      disabled: isCreatingConversation,
    }),
  ];
  const moreButtonDisabled = isEmpty(menuItems);
  const hasAttachments = collectConversationAttachments(messages).length > 0;
  const showThreadTabs = hasAttachments;
  const activeThreadTab = showThreadTabs ? threadTab : "conversation";

  return (
    <Box
      data-testid="floating-chat-conversation"
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <OverlaySpinner
        isLoading={isAssigning || isCreatingConversation}
        sx={{ position: "absolute" }}
      />
      <MessagingToolbar
        compact
        onBack={onBack}
        inputComponent={
          participant ? (
            <ConversationParticipantHeader
              participant={participant}
              disableTopMargin
            />
          ) : (
            <Typography fontWeight={600} noWrap>
              {t("messaging:messaging")}
            </Typography>
          )
        }
        actions={[
          {
            disabled: moreButtonDisabled,
            ariaLabel: t("actions", { ns: "settings" }),
            onClick: (e: MouseEvent<HTMLButtonElement>) =>
              setAnchorEl(e.currentTarget),
            icon: (
              <MoreIcon
                style={{
                  color: moreButtonDisabled
                    ? theme.palette.text.disabled
                    : theme.palette.text.secondary,
                }}
              />
            ),
          },
        ]}
      />
      {showThreadTabs && (
        <ConversationThreadTabs
          compact
          value={activeThreadTab}
          onChange={setThreadTab}
        />
      )}
      <ConversationDropZone disabled={inputDisabled} onFiles={addPendingFiles}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            ...(activeThreadTab === "attachments"
              ? { p: 0, gap: 0 }
              : { px: 1.5, pt: 1.5, pb: 1.5, gap: 2 }),
          }}
        >
          {activeThreadTab === "attachments" ? (
            <ConversationAttachmentsList messages={messages} compact />
          ) : (
            <>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Messages
                  virtuosoRef={virtuosoRef}
                  participant={participant}
                  messages={messages}
                  loadMore={loadMore}
                  loading={messagesLoading}
                  loadingMore={messagesLoadingMore}
                  compact
                />
              </Box>
              <MessagingComposer
                value={draft}
                onChange={onDraftChange}
                onSend={onSend}
                sendingDisabled={sendingDisabled}
                inputDisabled={inputDisabled}
                placeholder={composerPlaceholder}
                title={conversationTitle}
                titleLocked={titleLocked}
                onTitleChange={onTitleChange}
                rows={3}
                pendingFiles={pendingFiles}
                onAddFiles={addPendingFiles}
                onRemovePendingFile={removePendingFile}
              />
            </>
          )}
        </Box>
      </ConversationDropZone>
      <ActionMenu
        id="floating-conversation-action-menu"
        menuItems={menuItems}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      />
      <ForwardConversationDialog
        open={forwardOpen}
        teamTransferDisabled={teamTransferDisabled}
        onClose={() => setForwardOpen(false)}
        onConfirm={(options) => {
          setForwardOpen(false);
          transfer(options);
        }}
      />
      <ResolveConversationDialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onConfirm={(options) => {
          setResolveOpen(false);
          markAsDone(options);
        }}
      />
    </Box>
  );
};

export default FloatingChatConversation;
